import { useState } from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import {
  FaArrowRight,
  FaArrowUp,
  FaCheck,
  FaCircleCheck,
  FaCircleExclamation,
  FaCircleInfo,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
} from "react-icons/fa6";

export const isValidEmail = (s) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

const SMOOTH = "cubic-bezier(0.4, 0, 0.2, 1)";
const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

// ───── FloatInput ─────────────────────────────────────────────
export const FloatInput = ({
  label,
  value,
  onChange,
  type = "text",
  icon,
  rightSlot,
  valid,
  autoComplete,
  onKeyUp,
  onKeyDown,
  accent = "#04aec1",
  dark = false,
  ariaLabel,
}) => {
  const [focus, setFocus] = useState(false);
  const filled = value && value.length > 0;
  const isFloating = focus || filled;

  const baseBg = dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.85)";
  const focusBg = dark ? "rgba(255,255,255,0.07)" : "#ffffff";
  const borderDef = dark ? "rgba(255,255,255,0.10)" : "rgba(15,22,38,0.10)";
  const txt = dark ? "#e6ecf5" : "#0a0e1a";
  const labelTxt = dark ? "rgba(230,236,245,0.6)" : "rgba(15,22,38,0.55)";
  const iconLeft = icon ? "38px" : "14px";
  const inputPaddingLeft = icon ? 38 : 14;
  const inputPaddingRight = rightSlot || valid === true ? 44 : 14;

  return (
    <Box position="relative">
      <Box
        position="relative"
        bg={focus ? focusBg : baseBg}
        border="1px solid"
        borderColor={focus ? accent : borderDef}
        borderRadius="12px"
        transition={`border-color .2s ${SMOOTH}, background .2s ${SMOOTH}, box-shadow .2s ${SMOOTH}`}
        boxShadow={focus ? `0 0 0 4px ${accent}22, 0 8px 22px -10px ${accent}55` : "none"}
      >
        {icon && (
          <Box
            position="absolute"
            left="14px"
            top="50%"
            transform="translateY(-50%)"
            color={focus ? accent : labelTxt}
            fontSize="14px"
            transition={`color .2s ${SMOOTH}`}
            pointerEvents="none"
            display="grid"
            placeItems="center"
          >
            {icon}
          </Box>
        )}
        <Box
          as="label"
          position="absolute"
          left={iconLeft}
          top={isFloating ? "6px" : "50%"}
          transform={isFloating ? "translateY(0)" : "translateY(-50%)"}
          fontSize={isFloating ? "10px" : "13px"}
          fontWeight={isFloating ? 600 : 500}
          letterSpacing={isFloating ? "0.08em" : "0"}
          textTransform={isFloating ? "uppercase" : "none"}
          color={focus ? accent : labelTxt}
          transition={`all .2s ${SMOOTH}`}
          pointerEvents="none"
          bg="transparent"
        >
          {label}
        </Box>
        <Box
          as="input"
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          autoComplete={autoComplete}
          aria-label={ariaLabel || label}
          width="100%"
          background="transparent"
          border="none"
          outline="none"
          fontSize="14px"
          fontWeight={500}
          color={txt}
          padding={`22px ${inputPaddingRight}px 8px ${inputPaddingLeft}px`}
          boxSizing="border-box"
          sx={{
            caretColor: accent,
            "&:-webkit-autofill": {
              WebkitTextFillColor: txt,
              WebkitBoxShadow: `0 0 0 1000px ${dark ? "rgba(15,22,38,0.95)" : "#ffffff"} inset`,
              caretColor: accent,
            },
          }}
        />
        {rightSlot && (
          <Flex
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            align="center"
            gap="4px"
          >
            {rightSlot}
          </Flex>
        )}
        {valid === true && (
          <Box
            position="absolute"
            right={rightSlot ? "44px" : "12px"}
            top="50%"
            width="20px"
            height="20px"
            borderRadius="50%"
            bg="linear-gradient(135deg, #14b8a6, #0d9488)"
            display="grid"
            placeItems="center"
            color="white"
            sx={{
              animation: `sb-pop .3s ${SPRING}`,
              transform: "translateY(-50%)",
            }}
          >
            <FaCheck size={10} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ───── PasswordInput ──────────────────────────────────────────
export const PasswordInput = ({
  value,
  onChange,
  accent = "#04aec1",
  dark = false,
  label = "Password",
}) => {
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const handleKey = (e) => {
    if (typeof e.getModifierState === "function") {
      setCaps(e.getModifierState("CapsLock"));
    }
  };

  return (
    <Box>
      <FloatInput
        label={label}
        value={value}
        onChange={onChange}
        type={show ? "text" : "password"}
        icon={<FaLock />}
        accent={accent}
        dark={dark}
        autoComplete="current-password"
        onKeyUp={handleKey}
        onKeyDown={handleKey}
        rightSlot={
          <Box
            as="button"
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? "Hide password" : "Show password"}
            background="transparent"
            border="none"
            cursor="pointer"
            color={dark ? "rgba(230,236,245,0.6)" : "rgba(15,22,38,0.55)"}
            padding="8px"
            borderRadius="8px"
            fontSize="13px"
            display="grid"
            placeItems="center"
            transition={`color .2s ${SMOOTH}`}
            _hover={{ color: accent }}
          >
            {show ? <FaEyeSlash /> : <FaEye />}
          </Box>
        }
      />
      {caps && (
        <Flex
          mt="6px"
          fontSize="11px"
          fontWeight={600}
          color="#fbbf24"
          align="center"
          gap="6px"
          padding="4px 10px"
          background="rgba(251,191,36,0.10)"
          border="1px solid rgba(251,191,36,0.25)"
          borderRadius="8px"
          width="fit-content"
          sx={{ animation: "sb-slide-in .25s ease" }}
        >
          <FaArrowUp size={10} />
          Caps Lock is on
        </Flex>
      )}
    </Box>
  );
};

// ───── CheckBox ───────────────────────────────────────────────
export const CheckBox = ({ checked, onChange, accent = "#04aec1", dark = false }) => (
  <Box
    as="span"
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onClick={() => onChange(!checked)}
    onKeyDown={(e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        onChange(!checked);
      }
    }}
    width="16px"
    height="16px"
    borderRadius="5px"
    bg={
      checked
        ? `linear-gradient(135deg, ${accent}, ${accent}cc)`
        : dark
        ? "rgba(255,255,255,0.04)"
        : "rgba(15,22,38,0.04)"
    }
    border="1px solid"
    borderColor={checked ? accent : dark ? "rgba(255,255,255,0.18)" : "rgba(15,22,38,0.18)"}
    display="inline-grid"
    placeItems="center"
    cursor="pointer"
    transition={`all .2s ${SPRING}`}
    boxShadow={checked ? `0 4px 12px -4px ${accent}88` : "none"}
    flexShrink={0}
  >
    {checked && (
      <Box
        as="svg"
        width="10px"
        height="10px"
        viewBox="0 0 12 12"
        sx={{ display: "block" }}
      >
        <path
          d="M2 6.5 L5 9 L10 3.5"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    )}
  </Box>
);

// ───── SubmitBtn ──────────────────────────────────────────────
export const SubmitBtn = ({
  state = "idle",
  label = "Sign in",
  loadingLabel = "Signing in…",
  successLabel = "Welcome back",
  accent = "#04aec1",
  accentMid = "#6366f1",
  accentEnd = "#ec4899",
  onClick,
}) => {
  const [hover, setHover] = useState(false);
  const isLoading = state === "loading";
  const isSuccess = state === "success";
  const disabled = isLoading || isSuccess;

  return (
    <Box
      as="button"
      type="submit"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      width="100%"
      padding="14px 18px"
      bg={
        isSuccess
          ? "linear-gradient(135deg, #14b8a6, #0d9488)"
          : `linear-gradient(135deg, ${accent} 0%, ${accentMid} 60%, ${accentEnd} 120%)`
      }
      backgroundSize="200% 200%"
      backgroundPosition={hover && !disabled ? "100% 50%" : "0% 50%"}
      border="none"
      borderRadius="12px"
      color="white"
      fontSize="14px"
      fontWeight={700}
      letterSpacing="0.01em"
      cursor={disabled ? "default" : "pointer"}
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap="10px"
      transition={`all .3s ${SPRING}`}
      transform={hover && !disabled ? "translateY(-2px)" : "translateY(0)"}
      boxShadow={
        hover && !disabled
          ? `0 14px 32px -10px ${accentEnd}88, inset 0 1px 0 rgba(255,255,255,0.3)`
          : `0 10px 24px -8px ${accentMid}88, inset 0 1px 0 rgba(255,255,255,0.25)`
      }
      _disabled={{ opacity: 1 }}
    >
      {isSuccess ? (
        <>
          <Box
            as="svg"
            width="18px"
            height="18px"
            viewBox="0 0 18 18"
            sx={{ display: "block" }}
          >
            <path
              d="M4 9.5 L7.5 13 L14 5.5"
              fill="none"
              stroke="white"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeDasharray: 22,
                strokeDashoffset: 0,
                animation: "sb-draw .45s ease forwards",
              }}
            />
          </Box>
          {successLabel}
        </>
      ) : isLoading ? (
        <>
          <span className="sb-spinner" />
          {loadingLabel}
        </>
      ) : (
        <>
          {label}
          <FaArrowRight size={12} />
        </>
      )}
    </Box>
  );
};

// ───── Toast ──────────────────────────────────────────────────
const TOAST_STYLES = {
  success: {
    bg: "rgba(20,184,166,0.16)",
    border: "rgba(20,184,166,0.4)",
    icoColor: "#14b8a6",
    Icon: FaCircleCheck,
  },
  error: {
    bg: "rgba(239,68,68,0.14)",
    border: "rgba(239,68,68,0.4)",
    icoColor: "#ef4444",
    Icon: FaCircleExclamation,
  },
  info: {
    bg: "rgba(99,102,241,0.14)",
    border: "rgba(99,102,241,0.4)",
    icoColor: "#6366f1",
    Icon: FaCircleInfo,
  },
};

export const Toast = ({ toast, dark = false }) => {
  if (!toast) return null;
  const s = TOAST_STYLES[toast.status] || TOAST_STYLES.info;
  const Icon = s.Icon;
  const textColor = dark ? "#ffffff" : "#0a0e1a";
  return (
    <Flex
      role="alert"
      aria-live="polite"
      position="absolute"
      left="16px"
      right="16px"
      top="16px"
      bg={s.bg}
      border="1px solid"
      borderColor={s.border}
      backdropFilter="blur(12px)"
      borderRadius="12px"
      padding="10px 14px"
      align="flex-start"
      gap="10px"
      zIndex={50}
      color={textColor}
      sx={{ animation: "sb-slide-in .25s ease" }}
    >
      <Box color={s.icoColor} fontSize="14px" mt="2px" display="grid" placeItems="center">
        <Icon />
      </Box>
      <Box flex="1">
        <Text fontSize="12px" fontWeight={700} lineHeight="1.3">
          {toast.title}
        </Text>
        {toast.description && (
          <Text fontSize="11px" opacity={0.8} mt="2px" lineHeight="1.4">
            {toast.description}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

// ───── RetailerStrip ──────────────────────────────────────────
const RETAILERS = [
  { src: "/pnp.jpg", alt: "Pick n Pay" },
  { src: "/chk.jpg", alt: "Checkers" },
  { src: "/dis.jpg", alt: "Dis-Chem" },
  { src: "/cli.jpg", alt: "Clicks" },
];

export const RetailerStrip = ({ dark = true, compact = false }) => {
  const txt = dark ? "rgba(230,236,245,0.5)" : "rgba(15,22,38,0.45)";
  return (
    <Flex direction="column" align="center" gap={compact ? "8px" : "10px"}>
      <Text
        fontSize="9px"
        fontWeight={700}
        color={txt}
        textTransform="uppercase"
        letterSpacing="0.16em"
      >
        Trusted with feeds from
      </Text>
      <Flex gap={compact ? "12px" : "18px"} align="center" opacity={dark ? 0.85 : 0.7}>
        {RETAILERS.map((r) => (
          <Image
            key={r.alt}
            src={r.src}
            alt={r.alt}
            h={compact ? "18px" : "22px"}
            borderRadius="4px"
          />
        ))}
      </Flex>
    </Flex>
  );
};

// ───── MiniFooter ─────────────────────────────────────────────
export const MiniFooter = ({ dark = false, onPrivacy, onTerms, onHelp }) => {
  const txt = dark ? "rgba(230,236,245,0.45)" : "rgba(15,22,38,0.45)";
  const link = dark ? "rgba(230,236,245,0.7)" : "rgba(15,22,38,0.7)";
  const linkSx = {
    color: link,
    textDecoration: "none",
    fontSize: "10px",
    fontWeight: 500,
    transition: `color .2s ${SMOOTH}`,
    cursor: "pointer",
    _hover: { color: dark ? "white" : "#0a0e1a" },
  };
  return (
    <Flex justify="space-between" align="center" w="100%" fontSize="10px" color={txt} fontWeight={500}>
      <Box>© {new Date().getFullYear()} SpearBI</Box>
      <Flex gap="16px">
        <Box as="button" type="button" onClick={onPrivacy} sx={linkSx} background="transparent" border="none">
          Privacy
        </Box>
        <Box as="button" type="button" onClick={onTerms} sx={linkSx} background="transparent" border="none">
          Terms
        </Box>
        <Box as="button" type="button" onClick={onHelp} sx={linkSx} background="transparent" border="none">
          Help
        </Box>
      </Flex>
    </Flex>
  );
};

// ───── EnvelopeIcon (re-exported for use in Signin) ──────────
export const EnvelopeIcon = FaEnvelope;
